import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AdminAPI } from '@shared/types/api';

const queryKey = (eventTimeId: string | undefined) => ['admin', 'close-out-bars', eventTimeId];

export function useCloseOutBarConfig(eventTimeId: string | undefined) {
  return useQuery({
    queryKey: queryKey(eventTimeId),
    queryFn: async (): Promise<AdminAPI.CloseOutBarConfigResponse> => {
      return await api.get(`/admin/events/${encodeURIComponent(eventTimeId!)}/close-out-bars`);
    },
    enabled: !!eventTimeId,
  });
}

export function useSetCloseOutBar(eventTimeId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      neighborhoodId,
      barRestaurantId,
    }: {
      neighborhoodId: string;
      barRestaurantId: string | null;
    }): Promise<AdminAPI.SetCloseOutBarResponse> => {
      return await api.put(
        `/admin/events/${encodeURIComponent(eventTimeId!)}/close-out-bars/${encodeURIComponent(neighborhoodId)}`,
        { barRestaurantId }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(eventTimeId) });
    },
  });
}

export function useAutoAssignCloseOutBars(eventTimeId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AdminAPI.AutoAssignCloseOutBarsResponse> => {
      return await api.post(
        `/admin/events/${encodeURIComponent(eventTimeId!)}/close-out-bars/auto-assign`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(eventTimeId) });
    },
  });
}

export function useToggleCloseOutChat(eventTimeId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      neighborhoodId,
      action,
    }: {
      neighborhoodId: string;
      action: 'open' | 'close';
    }): Promise<AdminAPI.ToggleCloseOutChatResponse> => {
      return await api.post(
        `/admin/events/${encodeURIComponent(eventTimeId!)}/close-out-bars/${encodeURIComponent(neighborhoodId)}/${action}-chat`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(eventTimeId) });
    },
  });
}
